import { el, qs } from '../utils/dom.js';

/**
 * UndoHistoryPanel — visual timeline of all undo/redo commands.
 *
 * Shows a scrollable list of actions. The current state is highlighted.
 * Click any entry to jump directly to that point in history.
 * Future (redo-able) entries are dimmed.
 */
export class UndoHistoryPanel {
  /**
   * @param {import('../core/CommandHistory.js').CommandHistory} commandHistory
   * @param {Object} compositor — for requesting re-renders after undo/redo
   */
  constructor(commandHistory, compositor) {
    this._history = commandHistory;
    this._compositor = compositor;
    this._container = null;
    this._listEl = null;
    this._visible = false;

    // Re-render list when history changes
    this._history.onChange(() => {
      if (this._visible) this._render();
    });
  }

  /** Toggle the panel open/closed. */
  toggle() {
    this._visible ? this.hide() : this.show();
  }

  /** Show the panel. Creates the DOM if needed. */
  show() {
    if (!this._container) this._createDOM();
    this._container.style.display = 'flex';
    this._visible = true;
    this._render();
  }

  /** Hide the panel. */
  hide() {
    if (this._container) this._container.style.display = 'none';
    this._visible = false;
  }

  _createDOM() {
    this._container = el('div', {
      className: 'undo-history-panel',
      style: {
        position: 'absolute',
        right: '8px',
        top: '40px',
        bottom: '32px',
        width: '220px',
        background: 'var(--bg-panel, #1e1e2e)',
        border: '1px solid var(--border, #333)',
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        zIndex: '500',
        overflow: 'hidden',
        fontSize: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      },
    });

    // Header
    const header = el('div', {
      style: {
        padding: '8px 10px',
        borderBottom: '1px solid var(--border, #333)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: '600',
        color: 'var(--text, #ccc)',
      },
    });
    header.appendChild(el('span', {}, 'History'));
    const closeBtn = el('button', {
      style: { background: 'none', border: 'none', color: 'var(--text-muted, #888)', cursor: 'pointer', fontSize: '16px', padding: '0 2px', lineHeight: '1' },
      onClick: () => this.hide(),
    }, '\u00D7');
    header.appendChild(closeBtn);
    this._container.appendChild(header);

    // Scrollable list
    this._listEl = el('div', {
      style: {
        flex: '1',
        overflowY: 'auto',
        padding: '4px 0',
      },
    });
    this._container.appendChild(this._listEl);

    // Append to editor area
    const editorArea = qs('#editor-area') || document.body;
    editorArea.appendChild(this._container);
  }

  _render() {
    if (!this._listEl) return;
    this._listEl.innerHTML = '';

    const timeline = this._history.getTimeline();

    // "Initial state" entry at the top
    const initialEntry = this._createEntry({
      description: 'Initial State',
      index: -1,
      isCurrent: this._history.currentIndex === -1,
      isFuture: false,
    });
    this._listEl.appendChild(initialEntry);

    // Command entries
    for (const item of timeline) {
      this._listEl.appendChild(this._createEntry(item));
    }

    // Scroll current into view
    const current = this._listEl.querySelector('.undo-entry-current');
    if (current) current.scrollIntoView({ block: 'nearest' });
  }

  _createEntry(item) {
    const isCurrent = item.isCurrent;
    const isFuture = item.isFuture;

    const entry = el('div', {
      style: {
        padding: '4px 10px',
        cursor: 'pointer',
        color: isFuture ? 'var(--text-muted, #555)' : 'var(--text, #ccc)',
        background: isCurrent ? 'var(--accent, #3ba8ff)' : 'transparent',
        fontWeight: isCurrent ? '600' : '400',
        borderLeft: isCurrent ? '3px solid #fff' : '3px solid transparent',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'background 0.1s',
      },
      onClick: () => {
        this._history.goTo(item.index);
        this._compositor.requestRender();
      },
    });

    if (isCurrent) entry.className = 'undo-entry-current';

    // Hover effect (skip for current)
    if (!isCurrent) {
      entry.addEventListener('mouseenter', () => { entry.style.background = 'rgba(255,255,255,0.05)'; });
      entry.addEventListener('mouseleave', () => { entry.style.background = 'transparent'; });
    }

    entry.textContent = item.description;
    return entry;
  }
}
